<template>
  <SoDialog
    v-model="value"
    :card-title="cancelPhotoRequestTitle"
    :max-width="395"
  >
    <template v-if="cancellationSuccess">
      <div class="title-text mt-n4">
        <v-icon icon="check-circle success-icon mr-1" size="x-large"></v-icon>
        <h4 class="d-inline success-title">Photo Request Cancelled</h4>
      </div>
      <SoButton
        data-testid="cancellation-success-btn"
        class="mt-8"
        title="close"
        large
        type="primary"
        full-width
        @click="value = false"
      />
    </template>
    <template v-else>
      <p>
        Cancelling the photo request will allow you to send a new photo request
        to the customer, but you will need to update the Freshdesk ticket
        manually.
        <br />
        Are you sure you want to cancel the photo request?
      </p>
      <SoAlert
        v-if="cancellationError"
        ref="errorMessage"
        class="mt-5"
        type="error"
      >
        {{ errorMessage }}
      </SoAlert>
      <div class="d-flex gap-2 justify-end mt-9">
        <SoButton
          title="Back"
          type="secondary"
          large
          @click="$emit('backToPhotoRequests')"
        />
        <SoButton
          title="Cancel Photo Request"
          :loading="isLoading"
          large
          type="primary"
          data-testid="cancellation-btn"
          @click="submitCancellationRequest"
        />
      </div>
    </template>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { MeterReadingsMutator } from "@/mutators/meterReadings/MeterReadingsMutator"
  import { errorHandler, errorReporter } from "@/services/ErrorService"

  export default defineComponent({
    components: {
      SoDialog,
      SoButton,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      outboundRequest: {
        type: Object,
        default: () => ({}),
        required: true,
      },
    },
    emits: [
      "update:modelValue",
      "backToPhotoRequests",
      "onRefetchOutboundPhoto",
    ],
    data() {
      return {
        isLoading: false,
        cancellationSuccess: false,
        cancellationError: false,
        cancelOutboundRequest: MeterReadingsMutator.cancelOutboundRequest(),
        errorMessage: "",
        defaultErrorMessage: "There was an error with processing your request.",
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
          if (!value && this.cancellationSuccess) {
            this.$emit("onRefetchOutboundPhoto")
          }
        },
      },
      cancelPhotoRequestTitle() {
        return this.cancellationSuccess ? "" : "Cancel Photo Request"
      },
    },
    methods: {
      submitCancellationRequest() {
        this.cancellationSuccess = false
        this.cancellationError = false
        this.isLoading = true

        this.cancelOutboundRequest
          .mutateAsync(this.outboundRequest.outboundRequestId)
          .then(() => {
            // BE always returns success
            this.cancellationSuccess = true
          })
          .catch((error) => {
            this.handleSubmissionError(error)
          })
          .finally(() => {
            this.isLoading = false
          })
      },
      handleSubmissionError(error) {
        this.errorMessage = errorHandler.userMessage(
          error,
          "Failed to cancel the photo request",
        )

        if (!this.errorMessage) {
          this.errorMessage = this.defaultErrorMessage
        }

        errorReporter.report(new Error(this.errorMessage))
        this.cancellationError = true
      },
    },
  })
</script>

<style scoped>
  .success-icon {
    font-size: 26px;
    line-height: 28px;
    vertical-align: middle;
    color: rgb(var(--v-theme-success));
  }
  .success-title {
    vertical-align: middle;
  }
</style>
